import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * WebShellContext signals to shared Mini App components that they are
 * rendered inside the standalone web shell (`/app/*`) rather than the
 * Telegram Mini App runtime.
 *
 * The Mini App `<Layout>` defaults to `false` and renders its own
 * bottom `<NavBar />`. When `true`, the web `<AppShell>` already provides
 * `<WebNavBar />` and `<WebHeader />`, so the inner Mini App chrome must
 * be suppressed to avoid duplicate navigation bars (NN/g H4, H8).
 */
export interface WebShellContextValue {
  inWebShell: boolean;
}

const defaultValue: WebShellContextValue = { inWebShell: false };

export const WebShellContext = createContext<WebShellContextValue>(defaultValue);

export interface WebShellProviderProps {
  value?: WebShellContextValue;
  children: ReactNode;
}

export function WebShellProvider({
  value,
  children,
}: WebShellProviderProps): JSX.Element {
  return (
    <WebShellContext.Provider value={value ?? { inWebShell: true }}>
      {children}
    </WebShellContext.Provider>
  );
}

export function useWebShell(): WebShellContextValue {
  return useContext(WebShellContext);
}
